import { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import api from '../../services/api';
import { logger } from '../../lib/logger';
import { useWebSocket } from '../../context/WebSocketContext';

export const useNotifications = () => {
  const { isConnected } = useWebSocket();
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);

  const fetchNotifications = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/notifications?limit=20');
      setNotifications(response.data.notifications || []);
      setUnreadCount(response.data.unread_count || 0);
    } catch (error) {
      logger.error('Failed to fetch notifications:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications();
  }, [fetchNotifications]);

  // Poll periodically while WebSocket is connected so we pick up
  // server-side notifications even if the live socket misses one.
  useEffect(() => {
    const id = setInterval(() => {
      if (isConnected) fetchNotifications();
    }, 30000);
    return () => clearInterval(id);
  }, [isConnected, fetchNotifications]);

  const showToastFor = useCallback((notification) => {
    toast(notification.title, {
      description: notification.message,
      duration: 5000,
    });
  }, []);

  const handleIncomingWS = useCallback((message) => {
    if (message?.type !== 'notification' || !message.notification) return;
    setNotifications((prev) => [message.notification, ...prev]);
    setUnreadCount((prev) => prev + 1);
    showToastFor(message.notification);
  }, [showToastFor]);

  const handleMarkRead = useCallback(async (notificationId) => {
    try {
      await api.post('/notifications/mark-read', {
        notification_ids: [notificationId],
      });
      setNotifications((prev) =>
        prev.map((n) => (n.id === notificationId ? { ...n, read: true } : n))
      );
      setUnreadCount((prev) => Math.max(0, prev - 1));
    } catch (error) {
      logger.error('Failed to mark notification as read:', error);
    }
  }, []);

  const handleMarkAllRead = useCallback(async () => {
    try {
      await api.post('/notifications/mark-read', { mark_all: true });
      setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
      setUnreadCount(0);
      toast.success('All notifications marked as read');
    } catch (error) {
      logger.error('Failed to mark all as read:', error);
    }
  }, []);

  const handleDelete = useCallback(async (notificationId) => {
    try {
      await api.delete(`/notifications/${notificationId}`);
      const target = notifications.find((n) => n.id === notificationId);
      setNotifications((prev) => prev.filter((n) => n.id !== notificationId));
      if (target && !target.read) {
        setUnreadCount((prev) => Math.max(0, prev - 1));
      }
    } catch (error) {
      logger.error('Failed to delete notification:', error);
    }
  }, [notifications]);

  return {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    handleIncomingWS,
    handleMarkRead,
    handleMarkAllRead,
    handleDelete,
  };
};
